import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { compose } from 'redux';
import { flushSync } from 'react-dom';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Configure } from 'react-instantsearch';
import PropTypes from 'prop-types';

import { withViewport } from '../../util/uiHelpers';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getAlgoliaIndexNameForLang } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';

import { LayoutSingleColumn, Page } from '../../components';

import NotFoundPage from '../NotFoundPage/NotFoundPage';
import SystemErrorSection from '../../components/SystemErrorSection/SystemErrorSection';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import Header from './Header/Header';
import SearchSection from './SearchSection/SearchSection';
import StorefrontTabContent, {
  STOREFRONT_REVIEWS_SCROLL_ANCHOR_ID,
} from './StorefrontTabContent/StorefrontTabContent';
import Sidebar from './Sidebar/Sidebar';
import IntroText from './IntroText/IntroText';

import {
  isScrollingDisabled,
  manageDisableScrolling,
} from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  updateRecordsOrder,
  fetchUserAchievements,
  getStoreBlogs,
  getFetchStoreBlogsInProgress,
  getFetchStoreBlogsError,
  reorderStoreMedia,
  fetchStoreBlogsData,
  updateStoreCover,
} from './StoreProfilePage.duck';
import { isUsersEmailVerified } from '../TransactionPage/TransactionPage.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';

import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';
import {
  fetchTimeSlots,
  fetchTransactionLineItems,
} from '../ListingPage/ListingPage.duck';
import {
  addToCart,
  updateCart,
  removeFromCart,
  updateCartItemsBatch,
  groupCartItemsByAuthorAndDateRange,
} from '../../ducks/cart.duck';
import { resolveStoreCartView, getStorePageRouteParams } from '../../util/cartHelpers';
import { types as sdkTypes } from 'sharetribe-flex-sdk';
const { Money, UUID } = sdkTypes;
import css from './StoreProfilePage.module.css';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import {
  parseFiltersFromURL,
  parseStoreCartFromURL,
  updateFiltersInURL,
  updateStoreCartInURL,
  getMainContentKeyFromSearch,
  DATE_RANGE_PARAMS,
  STORE_CART_QUERY_PARAM,
  STORE_CART_OPEN_VALUE,
} from '../../util/dateRangeHelpers';
import {
  getVisibleCategoryIds,
  getVisibleCategoryAlgoliaFilter,
  isCategoryIdVisible,
} from '../../util/storeCategoryVisibility';
import { loadRentalDefaultDateRange } from '../../util/rentalDefaults';
import tz_lookup from 'tz-lookup';
import { SUPPORTED_LANGUAGES } from '../../util/constants';
import { getTranslatedPath } from '../../util/urlHelpers';
import { LanguageContext } from '../../context/LanguageContext';
import { transformProfileForLanguage } from '../../util/profileTranslations';
import { createStoreBlogPath, getStoreBlogId } from '../../util/storeBlogs';
import {
  generateStoreSlug,
  generateCitySlug,
  buildStoreSchema,
  buildFaqSchema,
  buildGallerySchema,
  buildStoreCanonicalUrl,
  buildTabRobotsDirective,
  buildBreadcrumbSchema,
  stripHtml,
  getOnPlatformReviewStats,
} from '../../util/seo';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import loadable from '@loadable/component';
import CartModal from '../../components/CartModal/CartModal';
// Lazy, exactly as ListingPageCarousel loads it - the all-orders panel is only ever
// needed once a shopper with more than one order opens the cart.
const AllCartItemsPanel = loadable(() =>
  import(/* webpackChunkName: "AllCartItemsPanel" */ '../../components/CartDrawer/AllCartItemsPanel')
);
import { startCheckoutFromItems } from '../CheckoutPage/checkoutFromCart';
import { getStoreCategoryPublicConfig } from '../../util/api';
import { scrollToElementWhenStable } from '../../util/scrollRestoration';
import NotificationContainer from './NotificationContainer/NotificationContainer';
import StoreLanguagePrompt from './StoreLanguagePrompt/StoreLanguagePrompt';
import VerificationModel from '../TransactionPage/TransactionPanel/VerificationModel';
import CustomizeStoreCoverModal from './CustomizeStoreCoverModal/CustomizeStoreCoverModal';
import StoreSearchScopeModal from './StoreSearchScopeModal/StoreSearchScopeModal';

// Algolia search client - shared across components
const ALGOLIA_CLIENT_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;
const getAlgoliaIndexName = () =>
  getAlgoliaIndexNameForLang(
    (typeof window !== 'undefined' && localStorage.getItem('appLanguage')) ||
    'en'
  );
const searchClient =
  ALGOLIA_CLIENT_ID && ALGOLIA_SEARCH_API_KEY
    ? algoliasearch(ALGOLIA_CLIENT_ID, ALGOLIA_SEARCH_API_KEY)
    : null;

const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth < 768;
/**
 * Offset below fixed topbar when scrolling filtered results into view on mobile (RAS-2550).
 * Was 400 — that undershot on stores with a tall bio/intro card above the sticky search bar,
 * landing mid-bio instead of at the results. This is sized like the other topbar-clearance
 * offsets below (topbar height + small buffer), not "reveal N px of the page above the bar".
 */
const MOBILE_RESULTS_SCROLL_OFFSET = 90;
/** Offset below fixed topbar when scrolling store search into view (desktop). */
const DESKTOP_SEARCH_SCROLL_OFFSET = 96;
/** Offset below fixed topbar when scrolling a focused date field into view (mobile/tablet). */
const MOBILE_DATE_FIELD_SCROLL_OFFSET = 80;
/** Same as desktop search: topbar clearance when scrolling reviews tab into view (viewportLarge+). */
const DESKTOP_REVIEWS_TAB_SCROLL_OFFSET = DESKTOP_SEARCH_SCROLL_OFFSET;
/**
 * Below 1024px, storefront tabs are fixed/sticky under the topbar — extra offset so the reviews
 * module is not hidden under chrome.
 */
const MOBILE_TABLET_REVIEWS_TAB_SCROLL_OFFSET = 144;

// Hard upper cap for a single storefront Algolia response.
const STORE_LISTINGS_PAGE_SIZE = 1000;

/** Tab suffixes StoreProfilePage renders itself (the Rentals tab has no suffix). */
const STORE_TAB_PATH_SUFFIX_RE = /\/(about|reviews|gallery|blog|faqs)\/?$/;
/**
 * "Stay on [store]" after a tab change: how long to keep looking for the store search bar
 * while the remounted page re-runs loadData and MainContent (with its InstantSearch
 * client) mounts. 80 x 50ms = 4s, then give up quietly - the shopper is on the Rentals
 * tab either way.
 */
const STORE_SEARCH_FOCUS_MAX_ATTEMPTS = 80;
const STORE_SEARCH_FOCUS_INTERVAL_MS = 50;
/**
 * Module-scoped on purpose, not a ref: each tab is its own named route (ProfilePage,
 * ProfilePageAbout, ...) and Routes.js keys <Route> by name, so "Stay on [store]" from
 * About REMOUNTS this component when it switches to the Rentals tab. A ref would be reset
 * before the effect that consumes it ever ran. Only ever set from a tap, so SSR never sees
 * it, and it is cleared once honoured or when the retry budget runs out.
 */
let pendingStoreSearchFocus = false;
/**
 * The all-carts row tapped for ANOTHER store, carried across the navigation to that
 * storefront (see handleSelectCartGroup). Module-scoped for the same reason as
 * pendingStoreSearchFocus: a store with a city segment and one without are different
 * named routes (ProfilePage vs ProfilePageNoCity), so the hand-over between them
 * REMOUNTS this component and a ref would be lost. Consumed by the cart resolution
 * effect once the target store's profile is in; stale after 30s so a hand-over that
 * never completes can't hijack a later visit.
 */
let pendingCartGroupTarget = null;
const PENDING_CART_GROUP_TARGET_TTL_MS = 30 * 1000;
const readPendingCartGroupTarget = () => {
  if (!pendingCartGroupTarget) return null;
  if (Date.now() - pendingCartGroupTarget.setAt > PENDING_CART_GROUP_TARGET_TTL_MS) {
    pendingCartGroupTarget = null;
    return null;
  }
  return pendingCartGroupTarget;
};

export const transformItemsToOriginalListings = items => {
  return items.reduce((listings, item) => {
    const { _geoloc, price, ...rest } = item;
    if (price && price.amount && price.currency) {
      const listing = {
        id: new UUID(rest.objectID),
        type: 'listing',
        attributes: {
          geolocation: { ..._geoloc },
          price: new Money(price.amount, price.currency),
          ...rest,
        },
      };
      return [...listings, listing];
    } else {
      return listings;
    }
  }, []);
};

const getAuthorImageVariant = (authorImage, variantName) => {
  const { variants = {} } = authorImage?.attributes || {};
  return variants[variantName];
};

const MainContent = props => {
  const mainContentRef = useRef(null);
  const {
    // configs and APIs
    config,
    routes,
    history,
    location,
    intl,
    params,

    // user and current user
    currentUser,
    userShowError,
    user,
    isCurrentUser,
    totalListings,

    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,

    // current user currency
    currentUserCurrency,
    onFetchTransactionLineItems,
    onFetchTimeSlots,

    // filters
    filters,
    onCategoryChange,
    onDateRangeChange,
    onSearchQueryChange,
    onListingFacetFiltersChange,

    // store location and address
    storeLocation,

    // key and store time zone
    key,
    storeTimeZone,
    defaultStoreName,
    storePath,
    // handlers
    onAddToCart,
    onUpdateCart,
    onUpdateRecordsOrder,
    updateOrderError,

    // cart items
    filteredCartItems,
    dateRangePickerRef,
    datePickerAnchorRef,
    reviewsReceived,
    totalReviewsReceived,
    achievementReviews,
    reviewsReceivedError,

    // store blogs
    storeBlogs,

    onManageDisableScrolling,
    searchSectionRef,
    pendingSearchSettledScrollRef,
    onScrollToSearchSection,
    onScrollToDateField,
    onSendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,

    /** Active UI locale — drives Algolia index so listing titles match LanguageContext */
    algoliaLocale,
  } = props;

  const { id } = params;
  const [ownerId, setOwnerId] = useState(id);

  useEffect(() => {
    setOwnerId(id);
  }, [id]);

  // Filter changes should bring the user back to the results section.

  if (userShowError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="StoreProfilePage.loadingDataFailed" />
      </p>
    );
  }

  // Build base filter for owner's listings.
  // When Show All is ON (showAllCategories !== false): show all store listings (no category filter).
  // When Show All is OFF: only show listings assigned to visible configured categories.
  const publicData = user?.attributes?.profile?.publicData || {};
  const storeCategories = publicData.storeCategories || [];
  const storeCategoryGroups = publicData.storeCategoryGroups || {};
  const hasStoreCategories = Array.isArray(storeCategories) && storeCategories.length > 0;
  const showAllCategories = publicData.showAllCategories !== false;

  let baseFilters = `user.id:${ownerId} AND state:published`;
  if (hasStoreCategories && !showAllCategories) {
    const { visibleCategoryIds } = getVisibleCategoryIds(storeCategories, storeCategoryGroups);
    const categoryFilter = getVisibleCategoryAlgoliaFilter(visibleCategoryIds, true);
    baseFilters = `${baseFilters} AND ${categoryFilter}`;
  }

  const algoliaIndexName = getAlgoliaIndexNameForLang(algoliaLocale || 'en');

  // Generate search input text for both SearchSection and Sidebar
  const searchInputText = intl.formatMessage(
    { id: 'StoreProfilePage.SearchSection.searchInputText' },
    { displayName: defaultStoreName }
  );

  if (!searchClient) {
    return null;
  }

  return (
    <InstantSearch
      key={ownerId}
      searchClient={searchClient}
      indexName={algoliaIndexName}
    >
      {/* typoTolerance "min": typos only when nothing matches exactly. Otherwise "tent" matches
          every "Rental" title on the store — same policy as the marketplace search. */}
      <Configure filters={baseFilters} hitsPerPage={STORE_LISTINGS_PAGE_SIZE} typoTolerance="min" />
      <div className={css.contentWrapper}>
        <Sidebar
          user={user}
          storeLocation={storeLocation}
          isCurrentUser={isCurrentUser}
          currentUser={currentUser}
          onSendVerificationEmail={onSendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
          reviewsReceived={reviewsReceived}
          totalReviewsReceived={totalReviewsReceived}
          achievementReviews={achievementReviews}
          reviewsReceivedError={reviewsReceivedError}
          history={history}
          location={location}
        />
        <div className={css.mainContent} ref={mainContentRef}>
          <IntroText
            storeAbout={publicData.storeAbout}
            seoDescription={publicData.seoDescription}
            storeName={publicData.storeName}
          />
          <SearchSection
            ref={searchSectionRef}
            // Store suggestions must respect exactly the same scope as the store's results —
            // this already carries user.id AND the category-visibility filter.
            suggestionFilters={baseFilters}
            config={config}
            params={params}
            routes={routes}
            history={history}
            user={user}
            intl={intl}
            filters={filters}
            dateRangeFilter={filters.dateRange}
            onCategoryChange={onCategoryChange}
            onDateRangeChange={onDateRangeChange}
            ownerId={ownerId}
            onAddToCart={onAddToCart}
            onUpdateRecordsOrder={onUpdateRecordsOrder}
            updateOrderError={updateOrderError}
            isCurrentUser={isCurrentUser}
            currentUser={currentUser}
            exchangeRates={exchangeRates}
            fetRatesError={fetRatesError}
            fetchRatesInProgress={fetchRatesInProgress}
            currentUserCurrency={currentUserCurrency}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onFetchTimeSlots={onFetchTimeSlots}
            storeTimeZone={storeTimeZone}
            filteredCartItems={filteredCartItems}
            onUpdateCart={onUpdateCart}
            searchInputText={searchInputText}
            onSearchQueryChange={onSearchQueryChange}
            onListingFacetFiltersChange={onListingFacetFiltersChange}
            dateRangePickerRef={dateRangePickerRef}
            datePickerAnchorRef={datePickerAnchorRef}
            storePath={storePath}
            totalListings={totalListings}
            onManageDisableScrolling={onManageDisableScrolling}
            pendingSearchSettledScrollRef={pendingSearchSettledScrollRef}
            onScrollToSearchSection={onScrollToSearchSection}
            onScrollToDateField={onScrollToDateField}
          />
        </div>
      </div>
    </InstantSearch>
  );
};

MainContent.propTypes = {
  config: PropTypes.object.isRequired,
  routes: PropTypes.arrayOf(propTypes.route).isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  params: PropTypes.shape({
    slug: PropTypes.string,
    city: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  currentUser: propTypes.currentUser,
  userShowError: propTypes.error,
  user: propTypes.user.isRequired,
  isCurrentUser: PropTypes.bool.isRequired,
  exchangeRates: PropTypes.array,
  fetRatesError: propTypes.error,
  fetchRatesInProgress: PropTypes.bool,
  currentUserCurrency: PropTypes.string.isRequired,
  onFetchTransactionLineItems: PropTypes.func.isRequired,
  onFetchTimeSlots: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    category: PropTypes.shape({
      categoryId: PropTypes.string,
      depth: PropTypes.number,
    }),
    dateRange: PropTypes.shape({
      startDate: PropTypes.instanceOf(Date),
      endDate: PropTypes.instanceOf(Date),
    }),
    listingFacetFilters: PropTypes.object,
  }),
  onCategoryChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onListingFacetFiltersChange: PropTypes.func,
  storeLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  storeAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  key: PropTypes.string,
  storeTimeZone: PropTypes.string,
  storePath: PropTypes.string,
  onAddToCart: PropTypes.func.isRequired,
  onUpdateRecordsOrder: PropTypes.func.isRequired,
  updateOrderError: propTypes.error,
  filteredCartItems: PropTypes.array,
  dateRangePickerRef: PropTypes.shape({ current: PropTypes.any }),
  datePickerAnchorRef: PropTypes.shape({ current: PropTypes.any }),
  storeBlogs: PropTypes.array,
  onSendVerificationEmail: PropTypes.func,
  sendVerificationEmailInProgress: PropTypes.bool,
  sendVerificationEmailError: propTypes.error,
  totalListings: PropTypes.number,
  onSearchQueryChange: PropTypes.func,
  pendingSearchSettledScrollRef: PropTypes.shape({ current: PropTypes.any }),
  onScrollToSearchSection: PropTypes.func,
  onScrollToDateField: PropTypes.func,
  reviewsReceivedError: propTypes.error,
  algoliaLocale: PropTypes.string,
};

function StoreProfilePageComponent(props) {
  const config = useConfiguration();
  const routes = useRouteConfiguration();
  const {
    intl,
    location,
    user,
    history,
    currentUser,
    userShowError,
    userShowInProgress,
    scrollingDisabled,
    reviews,
    queryReviewsInProgress,
    totalReviews,
    currentUserLocationInfo,
    onAddToCart,
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
    currentUserCurrency,
    onFetchTransactionLineItems,
    onFetchTimeSlots,
    params,
    onUpdateRecordsOrder,
    onReorderStoreMedia,
    updateOrderError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    achievementReviews = [],
    storeBlogs = [],
    fetchStoreBlogsInProgress,
    storeBlogsFetchCompleted,
    reviewsReceived,
    totalReviewsReceived,
    reviewsReceivedInProgress,
    reviewsReceivedPagination,
    reviewsReceivedError,
    dispatch,
    userId,
  } = props;

  const ROUTE_NAME_TO_TAB = {
    ProfilePage:              'home',
    ProfilePageAbout:         'about',
    ProfilePageReviews:       'reviews',
    ProfilePageGallery:       'gallery',
    ProfilePageFaqs:          'faqs',
    ProfilePageBlog:          'blog',
    ProfilePageNoCity:        'home',
    ProfilePageNoCityAbout:   'about',
    ProfilePageNoCityReviews: 'reviews',
    ProfilePageNoCityGallery: 'gallery',
    ProfilePageNoCityFaqs:    'faqs',
    ProfilePageNoCityBlog:    'blog',
    ProfilePageLegacy:        'home',
  };

  const activeTab = (() => {
    // Primary: matched route name (authoritative for path-based tab routes)
    const routeName = props.match?.route?.name;
    if (routeName && ROUTE_NAME_TO_TAB[routeName]) {
      return ROUTE_NAME_TO_TAB[routeName];
    }
    // Secondary: pathname suffix (withRouter doesn't include route name metadata)
    const pathMatch = location?.pathname?.match(/\/(about|reviews|gallery|blog|faqs)\/?$/);
    if (pathMatch?.[1]) {
      return pathMatch[1];
    }
    // Fallback: ?tab= query param (handles legacy links during transition)
    const parsed = parse(location?.search || '');
    const tab = typeof parsed.tab === 'string' ? parsed.tab : 'home';
    const allowed = ['home', 'about', 'reviews', 'gallery', 'blog', 'faqs'];
    return allowed.includes(tab) ? tab : 'home';
  })();

  // ── URL pattern disambiguation ─────────────────────────────────────────────
  //
  // Three URL patterns must be distinguished from params alone:
  //
  //   Three-segment (city present):
  //     /rental-owner/:city/:slug/:id
  //     params.city = city slug, params.slug = store slug, params.id = UUID
  //
  //   Two-segment (no city):
  //     /rental-owner/:slug/:id
  //     params.slug = store slug, params.city = undefined, params.id = UUID
  //
  //   Legacy (UUID-only):
  //     /rental-owner/:id
  //     params.slug = undefined, params.city = undefined, params.id = UUID
  //
  // Additional edge case: UUID mis-routed into params.city (defensive guard).

  const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const isLegacyUrl = !params.slug && !!params.id && UUID_REGEX.test(params.id);
  const isCityParamActuallyUUID = !!params.city && UUID_REGEX.test(params.city);

  // The real store owner UUID
  const resolvedUserId = isLegacyUrl
    ? params.id               // legacy /rental-owner/:id
    : isCityParamActuallyUUID
      ? params.city           // defensive: UUID mis-routed into city param
      : params.id;            // normal three-segment or two-segment

  // City from URL params — null when absent or misidentified as UUID
  // NOTE: canonical URLs use publicData.city, not this value.
  const resolvedCityFromParams = isCityParamActuallyUUID
    ? null
    : params.city || null;

  const dateRangePickerRef = useRef(null);
  const datePickerAnchorRef = useRef(null);
  const searchSectionRef = useRef(null);
  const pendingSearchSettledScrollRef = useRef(false);
  const reviewsPostLoadScrollDoneRef = useRef(false);
  const prevReviewsReceivedInProgressRef = useRef(undefined);

  const onScrollToSearchSection = useCallback(() => {
    if (typeof window === 'undefined') return;
    const isMobileViewport = window.innerWidth < 1024;
    const topOffset = isMobileViewport
      ? MOBILE_RESULTS_SCROLL_OFFSET
      : DESKTOP_SEARCH_SCROLL_OFFSET;
    // A filter/category change collapses CustomHits back to its first page of results
    // (and briefly to a skeleton grid) while the new query loads, so the page can be far
    // shorter than usual for a moment. A single synchronous scrollTo here would land on
    // that transient short layout - poll until the search bar's position stops shifting
    // (i.e. real results have settled) before finalizing, so we land below the sticky
    // header instead of snapping back up to the store banner/profile section (RAS-2550).
    scrollToElementWhenStable(() => searchSectionRef.current, topOffset);
  }, []);

  // ── Search-scope confirmation (RAS-2619) ────────────────────────────────────
  // The topbar's marketplace search (mobile icon, desktop input) hands us its SearchPage
  // navigation instead of running it. We park it until the shopper picks "Search all";
  // every other way out of the prompt drops it. Shown every time - no "don't ask again".
  const pendingMarketplaceSearchRef = useRef(null);
  const [isSearchScopeModalOpen, setIsSearchScopeModalOpen] = useState(false);
  // "Stay on [store]" when the store search bar isn't mounted yet (other tab, or the
  // Algolia client still loading): pendingStoreSearchFocus remembers the request; this
  // counter re-arms the effect below for the same-instance case (a tab switch remounts).
  const [storeSearchFocusRequest, setStoreSearchFocusRequest] = useState(0);

  const handleMarketplaceSearchIntercept = useCallback(navigateToSearch => {
    pendingMarketplaceSearchRef.current = navigateToSearch;
    setIsSearchScopeModalOpen(true);
  }, []);

  const handleSearchScopeClose = useCallback(() => {
    pendingMarketplaceSearchRef.current = null;
    setIsSearchScopeModalOpen(false);
  }, []);

  const handleSearchMarketplace = useCallback(() => {
    const navigateToSearch = pendingMarketplaceSearchRef.current;
    handleSearchScopeClose();
    if (typeof navigateToSearch === 'function') navigateToSearch();
  }, [handleSearchScopeClose]);

  /**
   * Focus the store's own search bar, ready to type. Returns false when it isn't in the
   * DOM (yet). Two inputs render (desktop + mobile bars, one hidden per breakpoint), so
   * pick the one that is actually laid out. Focus is done with preventScroll and the
   * scroll handled here: only when the bar isn't already on screen do we bring the
   * results section up (the bar is sticky, so it lands right under the topbar), and we
   * do it inside the input's 500ms post-focus grace so its collapse-keyboard-on-scroll
   * logic doesn't immediately blur what we just focused.
   */
  const focusStoreSearchInput = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    const section = searchSectionRef.current;
    if (!section) return false;
    const input = Array.from(
      section.querySelectorAll('input[data-store-search-input]')
    ).find(el => el.getClientRects().length > 0);
    if (!input) return false;

    input.focus({ preventScroll: true });

    const rect = input.getBoundingClientRect();
    const probeX = rect.left + Math.min(24, rect.width / 2);
    const probeY = rect.top + rect.height / 2;
    const hit = document.elementFromPoint(probeX, probeY);
    // elementFromPoint is null off-screen and returns the fixed topbar (or a modal layer)
    // when the input is underneath something - both mean "not usable where it is".
    const isVisibleOnScreen = !!hit && (hit === input || input.contains(hit));
    if (!isVisibleOnScreen) {
      onScrollToSearchSection();
    }
    return true;
  }, [onScrollToSearchSection]);

  const handleStayOnStore = useCallback(() => {
    pendingMarketplaceSearchRef.current = null;
    // Commit the close synchronously so the sheet is gone from the DOM before
    // focusStoreSearchInput probes what's on screen - otherwise elementFromPoint hits the
    // sheet itself and we'd scroll the results up on every "Stay", even when the bar was
    // already right there.
    flushSync(() => setIsSearchScopeModalOpen(false));
    // Still inside the tap's event dispatch, which is the only way iOS lets a
    // programmatic focus open the keyboard.
    if (focusStoreSearchInput()) return;

    pendingStoreSearchFocus = true;
    setStoreSearchFocusRequest(n => n + 1);
    if (activeTab !== 'home') {
      // Same URL shape and history semantics as StorefrontTabs' own tab links.
      const rentalsTabPath = location.pathname.replace(STORE_TAB_PATH_SUFFIX_RE, '');
      history.replace(rentalsTabPath);
    }
  }, [focusStoreSearchInput, activeTab, location.pathname, history]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!pendingStoreSearchFocus || activeTab !== 'home') return undefined;
    let attempts = 0;
    let timer = null;
    const tryFocus = () => {
      if (!pendingStoreSearchFocus) return;
      attempts += 1;
      if (focusStoreSearchInput() || attempts >= STORE_SEARCH_FOCUS_MAX_ATTEMPTS) {
        pendingStoreSearchFocus = false;
        return;
      }
      timer = window.setTimeout(tryFocus, STORE_SEARCH_FOCUS_INTERVAL_MS);
    };
    tryFocus();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [storeSearchFocusRequest, activeTab, focusStoreSearchInput]);

  const onScrollToDateField = useCallback(() => {
    if (typeof window === 'undefined') return;
    const el = datePickerAnchorRef.current;
    if (!el) return;
    const currentScrollY = window.scrollY ?? window.pageYOffset ?? 0;
    const isMobileViewport = window.innerWidth < 1024;
    const topOffset = isMobileViewport
      ? MOBILE_DATE_FIELD_SCROLL_OFFSET
      : DESKTOP_SEARCH_SCROLL_OFFSET;
    const targetTop =
      el.getBoundingClientRect().top + currentScrollY - topOffset;
    window.scrollTo({
      top: Math.max(0, targetTop),
      left: 0,
      behavior: 'auto',
    });
  }, []);

  const onScrollToReviewsSection = useCallback(() => {
    if (typeof window === 'undefined') return;
    const el = document.getElementById(STOREFRONT_REVIEWS_SCROLL_ANCHOR_ID);
    if (!el) return;
    const currentScrollY = window.scrollY ?? window.pageYOffset ?? 0;
    const isDesktopViewport = window.innerWidth >= 1024;
    const topOffset = isDesktopViewport
      ? DESKTOP_REVIEWS_TAB_SCROLL_OFFSET
      : MOBILE_TABLET_REVIEWS_TAB_SCROLL_OFFSET;
    const targetTop =
      el.getBoundingClientRect().top + currentScrollY - topOffset;
    window.scrollTo({
      top: Math.max(0, targetTop),
      left: 0,
      behavior: 'auto',
    });
  }, []);

  const scheduleScrollAfterFilterChange = useCallback(() => {
    pendingSearchSettledScrollRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onScrollToSearchSection();
      });
    });
  }, [onScrollToSearchSection]);

  // Scroll reviews module into view when opening the Reviews tab (URL or in-app navigation).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeTab !== 'reviews') {
      reviewsPostLoadScrollDoneRef.current = false;
      prevReviewsReceivedInProgressRef.current = undefined;
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onScrollToReviewsSection();
      });
    });
  }, [activeTab, onScrollToReviewsSection]);

  // One-time scroll after the first "reviews received" fetch finishes (layout height may change).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeTab !== 'reviews') return;

    const prev = prevReviewsReceivedInProgressRef.current;
    prevReviewsReceivedInProgressRef.current = reviewsReceivedInProgress;

    if (
      prev === true &&
      reviewsReceivedInProgress === false &&
      !reviewsPostLoadScrollDoneRef.current
    ) {
      reviewsPostLoadScrollDoneRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onScrollToReviewsSection();
        });
      });
    }
  }, [activeTab, reviewsReceivedInProgress, onScrollToReviewsSection]);

  // The store's zone, needed BEFORE the filters are parsed: the date filter is a pair
  // of store-zone midnights (see parseDateRangeFromURL), so reading it from the URL
  // has to use the same zone the picker writes it in. Read straight off the raw user
  // prop - `origin` is never translated - because the translated/derived profile
  // objects are only built further down. Null (no coordinates) falls back to
  // browser-local everywhere, consistently.
  const storeOrigin = user?.attributes?.profile?.publicData?.origin;
  const storeTimeZone =
    storeOrigin?.lat && storeOrigin?.lng ? tz_lookup(storeOrigin.lat, storeOrigin.lng) : null;

  // Parse initial filters from URL
  const initialFilters = parseFiltersFromURL(location?.search || '', storeTimeZone);
  const [filters, setFilters] = useState(initialFilters);

  // Owner-only modals: verification and customize store cover
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isVerificationInProgress, setIsVerificationInProgress] = useState(false);
  const [isVerificationFailed, setIsVerificationFailed] = useState(false);
  const [isCustomizeCoverOpen, setIsCustomizeCoverOpen] = useState(false);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);

  const [storeCategoryPublicConfig, setStoreCategoryPublicConfig] = useState(null);

  useEffect(() => {
    const uid = profileUser?.id?.uuid;
    if (!uid || typeof window === 'undefined') {
      setStoreCategoryPublicConfig(null);
      return;
    }
    getStoreCategoryPublicConfig(uid)
      .then(setStoreCategoryPublicConfig)
      .catch(() => setStoreCategoryPublicConfig(null));
  }, [profileUser?.id?.uuid]);

  const profileUserWithCategories = useMemo(() => {
    if (!profileUser?.id || !storeCategoryPublicConfig) {
      return profileUser;
    }
    const pd = profileUser.attributes?.profile?.publicData || {};
    return {
      ...profileUser,
      attributes: {
        ...profileUser.attributes,
        profile: {
          ...profileUser.attributes.profile,
          publicData: {
            ...pd,
            storeCategories: storeCategoryPublicConfig.storeCategories,
            storeCategoryGroups: storeCategoryPublicConfig.storeCategoryGroups,
            showAllCategories: storeCategoryPublicConfig.showAllCategories,
            showAllCustomCategories: storeCategoryPublicConfig.showAllCustomCategories,
            showAllInCategoryFilter: storeCategoryPublicConfig.showAllInCategoryFilter,
          },
        },
      },
    };
  }, [profileUser, storeCategoryPublicConfig]);

  const isCurrentUser =
    ensuredCurrentUser.id &&
    profileUser.id &&
    ensuredCurrentUser.id.uuid === profileUser.id.uuid;

  // Apply locale-specific translations to the profile for the active UI locale.
  // If no locale bucket exists yet, transformProfileForLanguage safely keeps original values.
  const { locale: visitorLocale } = React.useContext(LanguageContext);
  const translatedProfileUser = transformProfileForLanguage(
    profileUserWithCategories,
    visitorLocale
  );

  const { bio = '', displayName, publicData = {} } =
    (translatedProfileUser && translatedProfileUser.id && translatedProfileUser.attributes.profile) || {};

  const {
    storeName,
    city,
    rating,
    origin,
    storeAddress,
    storeDisplayPhoto,
    storeCoverImage,
    storeCoverBackgroundColor,
    defaultLanguage: storeDefaultLanguage,
  } = publicData;

  const aggregateReviewCount = Number(publicData?.reviews);
  const achievementReviewCount = Array.isArray(achievementReviews)
    ? achievementReviews.length
    : 0;
  const baseDisplayReviewCount =
    Number.isFinite(aggregateReviewCount) && aggregateReviewCount > 0
      ? Math.floor(aggregateReviewCount)
      : totalReviews;
  const displayReviewCount =
    Number(baseDisplayReviewCount || 0) + achievementReviewCount;

  // Sync URL changes back to state (browser back/forward)
  useEffect(() => {
    const urlFilters = parseFiltersFromURL(location?.search || '', storeTimeZone);
    setFilters(urlFilters);
  }, [location.search, storeTimeZone]);

  // When user loads with a category in URL, clear it if that category is hidden (direct link to hidden category)
  useEffect(() => {
    if (!profileUser?.id || !filters?.category?.categoryId || !history || !location) return;
    const pd = profileUserWithCategories?.attributes?.profile?.publicData || {};
    const storeCats = pd.storeCategories || [];
    const storeGroups = pd.storeCategoryGroups || {};
    const visible = isCategoryIdVisible(filters.category.categoryId, storeCats, storeGroups);
    // Hidden marketplace roots should never be selectable; hidden store categories are only cleared when Show All is OFF.
    if (!visible) {
      const nextFilters = { ...filters, category: null };
      setFilters(nextFilters);
      updateFiltersInURL(history, location, nextFilters, storeTimeZone);
    }
  }, [
    profileUser?.id,
    profileUserWithCategories,
    filters?.category?.categoryId,
    filters?.category?.depth,
    history,
    location,
    storeTimeZone,
  ]);

  // Fetch user achievements and email verification on client-side only
  useEffect(() => {
    // Only fetch on client-side (not during SSR)
    if (typeof window === 'undefined') {
      return;
    }

    // Only fetch if user is loaded and we have a valid userId
    if (!profileUser?.id || !props.userId) {
      return;
    }

    const userId = props.userId;

    // Fetch user achievements (includes achievement reviews)
    props.onFetchUserAchievements(userId);

    // Fetch email verification status
    props.onIsUsersEmailVerified(userId);
  }, [profileUser?.id, props.userId]);

  // Fetch store blogs on client-side after component mounts
  useEffect(() => {
    // Only fetch on client-side (not during SSR)
    if (typeof window === 'undefined') {
      return;
    }

    // Only fetch if user is loaded and we have a valid userId
    if (!profileUser?.id?.uuid) {
      return;
    }

    // Skip when a fetch is in flight, we already finished a fetch (even if []), or SSR/data
    // pre-filled blogs (length > 0 before our flag exists in older hydrated state).
    if (
      fetchStoreBlogsInProgress ||
      storeBlogsFetchCompleted ||
      storeBlogs.length > 0
    ) {
      return;
    }

    // Fetch store blogs
    props.onFetchStoreBlogs(profileUser?.id?.uuid);
  }, [
    profileUser?.id?.uuid,
    storeBlogs.length,
    fetchStoreBlogsInProgress,
    storeBlogsFetchCompleted,
  ]);

  // Client-side fallback: apply store default language when landing on store profile without locale in URL.
  // Use window.location.replace directly to avoid triggering context updates (setLocale) that can remount
  // the route and clear user state, causing infinite loading.
  const storeDefaultRedirectDoneRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!profileUser?.id || !location?.pathname || !params?.id) return;
    // if query has locale=en, do not redirect
    if (location.search.includes('locale=en')) return;
    const pathname = location.pathname;
    const hasNoLocalePrefix = /^\/rental-owner\/([^/]+\/){1,2}[^/]+(\/.*)?$/.test(pathname);
    if (!hasNoLocalePrefix) return;
    const effectiveStoreDefault =
      storeDefaultLanguage && SUPPORTED_LANGUAGES.includes(storeDefaultLanguage)
        ? storeDefaultLanguage
        : 'en';
    if (effectiveStoreDefault === 'en') return;
    if (storeDefaultRedirectDoneRef.current) return;
    storeDefaultRedirectDoneRef.current = true;
    const storeSlugForRedirect = generateStoreSlug(storeName || displayName);
    const citySlugForRedirect = generateCitySlug(storeAddress?.city ?? city);
    const basePath = citySlugForRedirect
      ? `/rental-owner/${citySlugForRedirect}/${storeSlugForRedirect}/${resolvedUserId}`
      : `/rental-owner/${storeSlugForRedirect}/${resolvedUserId}`;
    const tabSegment = activeTab !== 'home' ? `/${activeTab}` : '';
    const newPath =
      effectiveStoreDefault === 'en'
        ? `${basePath}${tabSegment}`
        : `/${effectiveStoreDefault}${basePath}${tabSegment}`;
    window.location.replace(newPath);
  }, [
    profileUser?.id,
    location?.pathname,
    location?.search,
    params?.id,
    params?.city,
    storeDefaultLanguage,
  ]);

  // Debounce timer refs to prevent race conditions from rapid filter changes
  const categoryChangeTimerRef = useRef(null);
  const dateRangeChangeTimerRef = useRef(null);
  const searchQueryChangeTimerRef = useRef(null);
  const listingFiltersChangeTimerRef = useRef(null);

  // Handlers that update both state and URL
  // Debounced to prevent race conditions from rapid clicks
  const handleCategoryChange = useCallback(
    (categoryId, depth) => {
      // Clear any pending category change
      if (categoryChangeTimerRef.current) {
        clearTimeout(categoryChangeTimerRef.current);
      }

      // Update state immediately for responsive UI.
      // Picking a category also clears any active keyword search: "camping tent"
      // AND "Party & Events" almost always returns nothing, and the shopper's
      // intent when tapping a different category mid-search is to browse that
      // category, not to intersect it with the old query (RAS-2591).
      setFilters(prev => {
        const newFilters = {
          ...prev,
          category: categoryId ? { categoryId, depth } : null,
          searchQuery: categoryId ? null : prev.searchQuery,
        };
        return newFilters;
      });


      // Debounce URL update to batch rapid changes
      categoryChangeTimerRef.current = setTimeout(() => {
        setFilters(current => {
          updateFiltersInURL(history, location, current, storeTimeZone);

          scheduleScrollAfterFilterChange();

          return current;
        });
      }, 100);
    },
    [history, location, scheduleScrollAfterFilterChange, storeTimeZone]
  );

  const handleDateRangeChange = useCallback(
    ({ startDate, endDate }) => {
      // Clear any pending date range change
      if (dateRangeChangeTimerRef.current) {
        clearTimeout(dateRangeChangeTimerRef.current);
      }

      // Update state immediately for responsive UI
      setFilters(prev => {
        const newFilters = {
          ...prev,
          dateRange: startDate && endDate ? { startDate, endDate } : null,
        };
        return newFilters;
      });

      // Debounce URL update to batch rapid changes.
      // Do NOT scroll after date change — user should stay on the card they are viewing.
      dateRangeChangeTimerRef.current = setTimeout(() => {
        setFilters(current => {
          updateFiltersInURL(history, location, current, storeTimeZone);
          return current;
        });
      }, 300); // Slightly longer for date range (user might be selecting dates)
    },
    [history, location, storeTimeZone]
  );

  // Prefill from a date range the user already confirmed on a listing page (localStorage,
  // 24h TTL - see util/rentalDefaults.js). Read-only: this page never writes that key.
  // Kept in an effect so the localStorage read is client-only and can't diverge from SSR
  // markup. Applied through handleDateRangeChange - exactly the path a manual selection
  // takes - so availability checks on the cards and the dates a cart item is created with
  // pick it up with no extra wiring. Dates already in the URL are the more specific
  // signal (shared link, back/forward) and win over the remembered range - UNLESS we're
  // landing here via the storeCart=open flag from a just-completed Rent Now (RAS-2583):
  // that item's dates were just written to this same remembered range, and the store's
  // own date filter may still hold an older selection from before the shopper opened the
  // listing. Without this override, the cart items are filtered by the stale store dates,
  // the item just added doesn't match, and the cart modal silently fails to open.
  useEffect(() => {
    const returningFromRentNow = parseStoreCartFromURL(location?.search || '');
    if (
      !returningFromRentNow &&
      initialFilters.dateRange?.startDate &&
      initialFilters.dateRange?.endDate
    ) {
      return;
    }
    // Arriving from the all-carts panel of another store (a remount when the two
    // stores are different named routes): the URL's dates ARE the tapped cart's dates
    // and must not be replaced by the remembered range.
    if (readPendingCartGroupTarget()) return;
    const remembered = loadRentalDefaultDateRange();
    if (!remembered) return;
    handleDateRangeChange(remembered);
  }, []);

  const handleSearchQueryChange = useCallback(
    searchQuery => {
      // Clear any pending search query change
      if (searchQueryChangeTimerRef.current) {
        clearTimeout(searchQueryChangeTimerRef.current);
      }

      // Update state immediately for responsive UI
      setFilters(prev => {
        const newFilters = {
          ...prev,
          searchQuery:
            searchQuery && searchQuery.trim().length > 0
              ? searchQuery.trim()
              : null,
        };
        return newFilters;
      });

      // Debounce URL update to batch rapid changes (typing)
      searchQueryChangeTimerRef.current = setTimeout(() => {
        setFilters(current => {
          updateFiltersInURL(history, location, current, storeTimeZone);

          scheduleScrollAfterFilterChange();

          return current;
        });
      }, 1000); // Longer debounce for search query (user is typing)
    },
    [history, location, scheduleScrollAfterFilterChange, storeTimeZone]
  );

  const handleListingFacetFiltersChange = useCallback(
    listingFacetFilters => {
      if (listingFiltersChangeTimerRef.current) {
        clearTimeout(listingFiltersChangeTimerRef.current);
      }

      setFilters(prev => ({
        ...prev,
        listingFacetFilters: listingFacetFilters || {},
      }));

      listingFiltersChangeTimerRef.current = setTimeout(() => {
        setFilters(current => {
          updateFiltersInURL(history, location, current, storeTimeZone);
          scheduleScrollAfterFilterChange();
          return current;
        });
      }, 100);
    },
    [history, location, scheduleScrollAfterFilterChange, storeTimeZone]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (categoryChangeTimerRef.current) {
        clearTimeout(categoryChangeTimerRef.current);
      }
      if (dateRangeChangeTimerRef.current) {
        clearTimeout(dateRangeChangeTimerRef.current);
      }
      if (searchQueryChangeTimerRef.current) {
        clearTimeout(searchQueryChangeTimerRef.current);
      }
      if (listingFiltersChangeTimerRef.current) {
        clearTimeout(listingFiltersChangeTimerRef.current);
      }
    };
  }, []);

  // Get store location for map
  const storeLocation = storeAddress?.origin || origin;

  // Handle contact user - navigate to inbox
  const handleContactUser = userToContact => { };

  const handleVerifyIdentityClick = useCallback(() => {
    setIsVerificationFailed(false);
    setIsVerificationModalOpen(true);
  }, []);

  const handleVerifyIdentityClose = useCallback(() => {
    setIsVerificationFailed(false);
    setIsVerificationInProgress(false);
    setIsVerificationModalOpen(false);
  }, []);

  const handleOpenCustomizeCover = useCallback(() => {
    setIsCustomizeCoverOpen(true);
  }, []);

  const handleCloseCustomizeCover = useCallback(() => {
    setIsCustomizeCoverOpen(false);
  }, []);

  const handleUpdateStoreCover = useCallback(
    payload => {
      return props.onUpdateUserProfile(payload);
    },
    [currentUser, props.onUpdateUserProfile]
  );

  // Cart UI state. ?storeCart=open is the single "cart requested" signal (nav-bar cart
  // buttons, the listing page's Rent Now when it came from this storefront, the
  // add-to-cart auto-open). Which SURFACE it opens - the single-order Store Cart panel
  // or the all-orders panel - is decided by resolveStoreCartView at the moment the flag
  // turns on (see the effect below), and only changes afterwards through explicit
  // gestures: the panel's "All orders" link, or tapping a row in the all-orders panel.
  // It deliberately does NOT re-resolve on every cart change while open - a date Apply
  // moves the group's items to new dates one render before the scope override catches
  // up, and re-resolving in between would bounce the shopper to the all-orders panel.
  const isStoreCartRequested = parseStoreCartFromURL(location?.search || '');
  const [storeCartView, setStoreCartView] = useState(null); // 'cart' | 'allOrders' | null
  // The (startDate, endDate) the Store Cart panel is scoped to when that isn't simply
  // the page's date filter: the row tapped in the all-orders panel, the booking just
  // added, or the new dates after an Apply. Cleared whenever the cart UI closes so the
  // next open re-evaluates against the current filter (mirrors ListingPageCarousel's
  // cartDrawerOverride lifecycle).
  const [cartScopeOverride, setCartScopeOverride] = useState(null);

  // Get cart state from Redux
  const cartState = props.cart || {};
  const {
    items: cartItems,
    addToCartInProgress,
    notification,
  } = cartState;
  // cart.duck's initialState.items is null until fetchCartItems has answered - only
  // then can "is the cart empty?" be answered honestly.
  const isCartLoaded = cartItems != null;

  // Get current store's authorId
  const storeAuthorId = useMemo(() => {
    return profileUser?.id?.uuid;
  }, [profileUser]);

  // Filter cart items for current store only
  const storeCartItems = useMemo(() => {
    if (!storeAuthorId || !cartItems?.[storeAuthorId]) return [];
    return cartItems[storeAuthorId] || [];
  }, [cartItems, storeAuthorId]);

  // This store's (author, date range) groups, newest first - the same grouping the
  // listing page's drawer and the all-orders panel use, so every cart surface agrees
  // on what an "order" is.
  const storeCartGroups = useMemo(
    () =>
      storeAuthorId && storeCartItems.length
        ? groupCartItemsByAuthorAndDateRange({ [storeAuthorId]: storeCartItems })
        : [],
    [storeAuthorId, storeCartItems]
  );
  // Does the cart hold more than one order across ALL stores? Drives the panel's
  // "All orders" link (hidden when the only order is the one on screen).
  const hasOtherCartGroups = useMemo(
    () => groupCartItemsByAuthorAndDateRange(cartItems || {}).length > 1,
    [cartItems]
  );
  const hasAnyCartItems = useMemo(
    () => Object.values(cartItems || {}).some(items => items?.length > 0),
    [cartItems]
  );

  // The one group the Store Cart panel shows: the override's exact range when a row /
  // just-added booking / date Apply set one, else the group on the page's date filter.
  const cartPanelScope = cartScopeOverride || filters.dateRange;
  const cartPanelItems = useMemo(
    () => resolveStoreCartView(storeCartGroups, cartPanelScope, hasAnyCartItems).group?.items || [],
    [storeCartGroups, cartPanelScope, hasAnyCartItems]
  );

  // Items booked for exactly the current date filter. This is what the listing CARDS
  // need (a card's quantity stepper / "in cart" state refers to the dates the card
  // would add for). The panel itself is scoped via cartPanelItems above, which can
  // differ once a row is picked in the all-orders panel.
  const filteredCartItems = useMemo(() => {
    if (!filters.dateRange?.startDate || !filters.dateRange?.endDate) {
      return []; // No date range selected, show no items
    }

    return storeCartItems.filter(item => {
      if (!item.bookingDates?.startDate || !item.bookingDates?.endDate) {
        return false;
      }

      // Same calendar day in the STORE's zone - both sides are store-zone midnights,
      // so comparing in the browser's zone would split them across days for a
      // shopper west of the store.
      const tz = storeTimeZone || undefined;
      const toDay = d => (tz ? moment(d).tz(tz) : moment(d));
      return (
        toDay(item.bookingDates.startDate).isSame(toDay(filters.dateRange.startDate), 'day') &&
        toDay(item.bookingDates.endDate).isSame(toDay(filters.dateRange.endDate), 'day')
      );
    });
  }, [storeCartItems, filters.dateRange, storeTimeZone]);
  const closeCartModal = useCallback(() => {
    setStoreCartView(null);
    setCartScopeOverride(null);
    updateStoreCartInURL(history, location, false);
  }, [history, location]);

  // Resolves which surface to show whenever the cart is requested (the URL flag turns
  // on) and the cart data is there to decide with. Reads the latest groups/filter
  // through a ref rather than listing them as dependencies - see the state comment
  // above for why this must not re-run on every cart change while open. Mirroring the
  // flag back to `null` on removal is what makes the browser back button close either
  // panel, which CartModal's close-transition handling (persisting pending quantity
  // edits) relies on.
  const cartResolutionRef = useRef(null);
  cartResolutionRef.current = { storeCartGroups, cartPanelScope, hasAnyCartItems };
  // True only for the very first resolution, and only when the page mounted with the
  // flag already set - a later tap of the cart button must resolve against the live
  // filter (which the shopper may since have cleared), never a remembered range.
  const pendingLandingResolutionRef = useRef(isStoreCartRequested);
  // storeAuthorId is a dependency for the all-orders panel's hand-over to another
  // store (handleSelectCartGroup below): the flag stays on across that navigation, so
  // the arrival of the target store's profile is the signal to resolve again. It is
  // otherwise stable for the life of the page (it only changes when the store does).
  useEffect(() => {
    if (!isStoreCartRequested) {
      setStoreCartView(null);
      setCartScopeOverride(null);
      pendingCartGroupTarget = null;
      return;
    }
    if (!isCartLoaded) return;
    const { storeCartGroups: groups, cartPanelScope, hasAnyCartItems: hasAny } =
      cartResolutionRef.current;
    const pendingTarget = readPendingCartGroupTarget();
    if (pendingTarget) {
      // Mid hand-over: the profile is still the previous store's, or briefly absent
      // while the new one loads. Hold the closed panel until the target store is in.
      if (storeAuthorId !== pendingTarget.authorId) return;
      pendingCartGroupTarget = null;
      // This IS the landing resolution when the hand-over remounted the page.
      pendingLandingResolutionRef.current = false;
      const targetScope = { startDate: pendingTarget.startDate, endDate: pendingTarget.endDate };
      setCartScopeOverride(targetScope);
      setStoreCartView(resolveStoreCartView(groups, targetScope, hasAny).view);
      return;
    }
    let scope = cartPanelScope;
    // Landing here with the flag already set (the listing page's Rent Now for this
    // storefront, RAS-2583): the remembered rental range is what the page's date
    // filter is about to be prefilled with (see the loadRentalDefaultDateRange effect
    // above, which runs in this same commit but whose setFilters only lands a render
    // later) - and it's the range the booking was just added on. Scope to it now so
    // the panel opens straight onto that booking instead of resolving against dates
    // that are one render stale. Landing only: later opens use the live filter.
    if (pendingLandingResolutionRef.current) {
      pendingLandingResolutionRef.current = false;
      // The remembered range wins only when it actually names one of this store's
      // carts. Otherwise fall back to the page's own scope (the URL's dates) - that
      // is how the cart page's store row and the all-carts hand-over arrive here,
      // asking for one specific cart, possibly with a different remembered range
      // left behind by an unrelated listing visit.
      const remembered = loadRentalDefaultDateRange();
      if (remembered && resolveStoreCartView(groups, remembered, hasAny).group) {
        scope = remembered;
        setCartScopeOverride(remembered);
      } else if (remembered && !resolveStoreCartView(groups, scope, hasAny).group) {
        scope = remembered;
        setCartScopeOverride(remembered);
      }
    }
    setStoreCartView(resolveStoreCartView(groups, scope, hasAny).view);
  }, [isStoreCartRequested, isCartLoaded, storeAuthorId]);


  const isCartModalOpen = storeCartView === 'cart';
  const isAllOrdersPanelOpen = storeCartView === 'allOrders';

  // Safety net for the Store Cart panel: it must never sit open on "Your cart is
  // empty" while the shopper does have carts (client report 2026-09-22 after the
  // all-carts hand-over). Whatever got the view and the scope out of step - a scope
  // that no longer names a group, a cart that arrived after the view was decided -
  // re-resolve against the live scope, and fall back to the all-carts list when
  // nothing matches. Deferred a beat rather than immediate, because a date Apply
  // legitimately leaves the panel empty for one render (the items move to new dates
  // before the scope override catches up - see the state comment above) and must
  // not be bounced to the list.
  const isCartPanelEmpty = isCartModalOpen && cartPanelItems.length === 0 && hasAnyCartItems;
  useEffect(() => {
    if (!isCartPanelEmpty) return undefined;
    const timer = setTimeout(() => {
      const { storeCartGroups: groups, cartPanelScope: liveScope, hasAnyCartItems: hasAny } =
        cartResolutionRef.current;
      const resolved = resolveStoreCartView(groups, liveScope, hasAny);
      if (!resolved.group) {
        setCartScopeOverride(null);
        setStoreCartView('allOrders');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [isCartPanelEmpty]);

  // "All orders" link in the panel header - same surface swap as the listing page.
  const handleViewAllOrders = useCallback(() => {
    setCartScopeOverride(null);
    setStoreCartView('allOrders');
  }, []);

  // A row tapped in the all-orders panel. This store's own order: scope the panel to
  // that exact group and swap back to it. Another store's order: this panel is built
  // around THIS store (its owner, its Rent Now), so hand over to that storefront with
  // the row's dates written into its date filter and the cart flag set.
  //
  // That hand-over is a push to another store URL. Routes.js keys <Route> by name, so
  // between two stores on the SAME named route (both with a city segment, say) this
  // instance survives and only loadData re-runs - the cart flag is already on in both
  // URLs, so the resolution effect wouldn't fire on its own and the all-orders panel
  // just stayed open over the reloaded page (client bug report 2026-09-18: "it just
  // refreshes the page instead of taking me into the store"). Between a store WITH a
  // city segment and one without (ProfilePage vs ProfilePageNoCity) the page
  // REMOUNTS instead. pendingCartGroupTarget (module-scoped, so it survives both)
  // carries the tapped group across: the panel closes now so the new storefront is
  // visible while it loads, and the resolution effect re-resolves onto that group
  // once the profile for the target store is in.
  const handleSelectCartGroup = useCallback(
    group => {
      if (group.authorId === storeAuthorId) {
        setCartScopeOverride({ startDate: group.startDate, endDate: group.endDate });
        setStoreCartView('cart');
        return;
      }
      const route = getStorePageRouteParams(group.items[0]?.listing?.author);
      if (!route) return;
      // Calendar days in the booking's own zone - the target store page reads them
      // back in its zone (same convention as getRentMoreItemsUrl); a browser-local
      // day would land a cross-zone shopper on the wrong day.
      const groupTimeZone = group.items[0]?.timeZone;
      const toDay = d => (groupTimeZone ? moment(d).tz(groupTimeZone) : moment(d)).format('YYYY-MM-DD');
      pendingCartGroupTarget = {
        authorId: group.authorId,
        startDate: group.startDate,
        endDate: group.endDate,
        setAt: Date.now(),
      };
      setStoreCartView(null);
      history.push(
        createResourceLocatorString(route.name, routes, route.params, {
          [DATE_RANGE_PARAMS.START_DATE]: toDay(group.startDate),
          [DATE_RANGE_PARAMS.END_DATE]: toDay(group.endDate),
          [STORE_CART_QUERY_PARAM]: STORE_CART_OPEN_VALUE,
        })
      );
    },
    [storeAuthorId, history, routes]
  );

  // A successful date Apply inside the panel: the group's items now live under NEW
  // dates, so re-scope to them or the panel's (store, old dates) lookup would come up
  // empty despite the write succeeding (RAS-2511/2512, same as the listing drawer).
  // The page's own date filter is deliberately left alone - the cart is the cart, the
  // filter is the filter; the shopper re-filters if they want the grid to follow.
  const handleCartDatesApplied = useCallback(newBookingDates => {
    setCartScopeOverride({
      startDate: newBookingDates.startDate,
      endDate: newBookingDates.endDate,
    });
  }, []);

  // Auto-open modal when item is added to cart for this store (once per success to avoid update loop)
  const lastSuccessNotificationRef = useRef(null);
  useEffect(() => {
    // RAS-2582: only an "add to cart" success should auto-open the modal. A "cart
    // updated" notification (e.g. from a quantity edit persisted when the Store
    // Cart closes) must be ignored here, or closing the cart right after editing a
    // quantity would immediately reopen it.
    const isAddNotification =
      notification?.type === 'success' && notification?.operation !== 'update';

    if (
      !isAddNotification ||
      filteredCartItems.length === 0 ||
      addToCartInProgress
    ) {
      if (!isAddNotification) {
        lastSuccessNotificationRef.current = null;
      }
      return;
    }
    if (lastSuccessNotificationRef.current === notification) {
      return;
    }
    lastSuccessNotificationRef.current = notification;
    // Scope explicitly to the booking just added (it was added on the page's current
    // dates - that's what the cards add for) rather than trusting "most recent", the
    // same RAS-2520 reasoning as the listing page. Setting the URL flag is what opens
    // the surface, via the resolution effect above.
    setCartScopeOverride({
      startDate: filters.dateRange.startDate,
      endDate: filters.dateRange.endDate,
    });
    setStoreCartView('cart');
    updateStoreCartInURL(history, location, true);
  }, [
    notification,
    filteredCartItems.length,
    addToCartInProgress,
    history,
    location,
  ]);

  const handleAddToCart = useCallback(
    async params => {
      try {
        const browserTimeZone =
          typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : null;

        const paramsResolved = {
          ...params,
          // Store TZ only exists when origin has lat/lng; hits may omit nested user.id.
          timeZone: params.timeZone || storeTimeZone || browserTimeZone || 'UTC',
          authorId: params.authorId || storeAuthorId,
        };

        await onAddToCart(config, paramsResolved);
        // Modal will auto-open via useEffect watching notification
      } catch (error) {
        console.error('Error adding to cart:', error);
        // TODO: Show user-friendly error message (e.g., toast notification)
      }
    },
    [onAddToCart, config, storeAuthorId, storeTimeZone]
  );

  const handleUpdateCart = useCallback(
    updateData => {
      props.onUpdateCart({
        ...updateData,
        authorId: storeAuthorId,
      });
    },
    [props.onUpdateCart, storeAuthorId]
  );

  const handleRemoveFromCart = useCallback(
    itemId => {
      props.onRemoveFromCart(itemId);
      // Close the panel when the order it shows becomes empty
      if (cartPanelItems.length <= 1) {
        closeCartModal();
      }
    },
    [closeCartModal, props.onRemoveFromCart, cartPanelItems.length]
  );
  const handleRentNow = useCallback(
    async (updatedItemsFromModal = null) => {
      // Use updated items from modal if provided, otherwise fall back to Redux
      const itemsToUse = updatedItemsFromModal || cartPanelItems;

      if (!itemsToUse || !itemsToUse.length) return;

      // Fetch line items first

      // Navigate to checkout with updated items
      await startCheckoutFromItems({
        items: itemsToUse, // Use updated items
        activeAuthorId: storeAuthorId,
        lineItems: { [storeAuthorId]: lineItems },
        fetchLineItemsInProgress: {},
        setItemsInProgress: false,
        removeFromCartInProgress: false,
        currentUser: ensuredCurrentUser,
        routeConfiguration: routes,
        dispatch: props.dispatch,
        history,
      });
    },
    [
      cartPanelItems,
      storeAuthorId,
      onFetchTransactionLineItems,
      routes,
      history,
      ensuredCurrentUser,
      props.dispatch,
    ]
  );

  const defaultStoreName = storeName ? storeName : displayName;

  const storePath = `${location.pathname}${location.search}${location.hash}`;
  const rootUrl = config.marketplaceRootURL;
  const storeUserId = resolvedUserId || profileUser?.id?.uuid;
  // Authoritative city — always from publicData, never from URL params.
  // The URL may have a different city slug if someone edits the URL manually.
  // publicData.city is the source of truth for canonical URL generation.
  const authorizedCity = publicData?.storeAddress?.city || publicData?.city || null;

  // Stable canonical base URL — no query params, no hash, no filter state
  const storeBaseUrl = buildStoreCanonicalUrl({
    rootUrl,
    storeName: storeName || displayName,
    city: authorizedCity,
    userId: storeUserId,
    activeTab: 'home',
  });

  // Per-tab canonical URL — self-referencing for non-English pages per Google's hreflang spec.
  // storeBaseUrl stays English and is used for schema.org @id cross-references only.
  const canonicalUrl = intl.locale !== 'en'
    ? `${rootUrl}${location.pathname.replace(/\/$/, '')}`
    : buildStoreCanonicalUrl({
        rootUrl,
        storeName: storeName || displayName,
        city: authorizedCity,
        userId: storeUserId,
        activeTab,
      });

  // Blog collection URL (path-based, replaces ?tab=blog)
  const storeBlogCollectionUrl = buildStoreCanonicalUrl({
    rootUrl,
    storeName: storeName || displayName,
    city: authorizedCity,
    userId: storeUserId,
    activeTab: 'blog',
  });

  // storeUrl used by child components — point to stable base URL
  const storeUrl = storeBaseUrl;

  // Prefer landscape banner for OG (renders correctly at 1200×630).
  // Fall back to profile display photo, then legacy profileImage.
  const socialImageUrl =
    storeDisplayPhoto?.secure_url ||
    storeCoverImage?.secure_url ||
    publicData?.profileImage?.secure_url ||
    null;

  // Enforce Cloudinary OG dimensions. Only transforms Cloudinary URLs.
  // Idempotent: calling twice on an already-transformed URL is safe because
  // the regex anchors on the literal '/upload/' segment.
  const toOgImageUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
    if (!rawUrl.includes('res.cloudinary.com')) return rawUrl;
    if (!rawUrl.includes('/upload/')) return rawUrl;
    // Replace existing transform chain or insert one if absent
    return rawUrl.replace(
      '/upload/',
      '/upload/w_1200,h_630,c_fill,f_auto,q_auto/'
    );
  };

  const ogImageUrl = toOgImageUrl(socialImageUrl);

  const coverAltText =
    storeDisplayPhoto?.seoTags?.altText ||
    storeCoverImage?.seoTags?.altText ||
    (storeName && authorizedCity
      ? `${storeName} store banner in ${authorizedCity}`
      : storeName || '');

  const socialSharing = {
    title: null,       // set after pageTitle is derived below
    description: null, // set after pageDescription is derived below
    images1200: ogImageUrl
      ? [{ url: ogImageUrl, width: 1200, height: 630, alt: coverAltText }]
      : null,
    images600: ogImageUrl
      ? [{ url: ogImageUrl, width: 600, height: 314, alt: coverAltText }]
      : null,
  };

  // Schema image: prefer store display photo, then profile image variant
  const schemaImage = socialImageUrl
    ? { url: socialImageUrl }
    : getAuthorImageVariant(profileUser.profileImage, 'scaled-small');

  const { reviewCount: seoReviewCount, averageRating: seoAverageRating } = useMemo(
    () =>
      getOnPlatformReviewStats({
        reviews: reviewsReceived,
        totalReviewCount: totalReviewsReceived,
      }),
    [reviewsReceived, totalReviewsReceived]
  );

  // ── Tab-specific page title ────────────────────────────────────────────────
  const buildStorePageTitle = ({ activeTab: tab, publicData: pd, marketplaceName: mpName }) => {
    const name = pd?.storeName || defaultStoreName || 'Store';
    const mp = mpName || '';
    const c = pd?.city || '';

    switch (tab) {
      case 'about':
        return `${name} — About | ${mp}`;
      case 'reviews': {
        const r = seoReviewCount;
        const rt = seoAverageRating;
        return r > 0 && rt > 0
          ? `${name} — ${r} Reviews (${Number(rt).toFixed(1)}★) | ${mp}`
          : r > 0
            ? `${name} — ${r} Reviews | ${mp}`
            : `${name} — Reviews | ${mp}`;
      }
      case 'gallery':
        return `${name} — Gallery | ${mp}`;
      case 'faqs':
        return `${name} — FAQs | ${mp}`;
      case 'blog':
        return `${name} — Blog | ${mp}`;
      case 'home':
      default: {
        // Guard against verbatim placeholder values that owners leave unchanged.
        // Any single generic word (case-insensitive) is treated as a placeholder.
        const TITLE_PLACEHOLDERS = new Set([
          'title', 'name', 'store', 'test', 'my store', 'store name', 'your title',
        ]);
        const rawSeoTitle = stripHtml(pd?.seoTitle).trim().slice(0, 200);
        const isTitlePlaceholder =
          !rawSeoTitle || TITLE_PLACEHOLDERS.has(rawSeoTitle.toLowerCase());
        if (!isTitlePlaceholder) return rawSeoTitle;
        return c
          ? `${name} | ${c} Rentals | ${mp}`
          : `${name} | ${mp}`;
      }
    }
  };

  // ── Tab-specific meta description ────────────────────────────────────────────
  const truncate155 = text => {
    if (!text) return '';
    return text.length > 154 ? text.slice(0, 154).trimEnd() + '…' : text;
  };

  const buildStorePageDescription = ({ activeTab: tab, publicData: pd, marketplaceName: mpName }) => {
    const name = pd?.storeName || defaultStoreName || 'Store';
    const mp = mpName || '';
    const c = pd?.storeAddress?.city || pd?.city || '';
    const aboutPlain = stripHtml(pd?.storeAbout);

    switch (tab) {
      case 'about':
        return aboutPlain.length > 0
          ? truncate155(aboutPlain)
          : `Learn more about ${name} on ${mp}.`;
      case 'reviews': {
        const r = seoReviewCount;
        const rt = seoAverageRating;
        return r > 0 && rt > 0
          ? `Read ${r} reviews for ${name} on ${mp}. Rated ${Number(rt).toFixed(1)} out of 5.`
          : r > 0
            ? `Read ${r} customer reviews for ${name} on ${mp}.`
            : `Read customer reviews for ${name} on ${mp}.`;
      }
      case 'gallery':
        return `Browse photos and media from ${name} on ${mp}.`;
      case 'faqs': {
        const faqCount = pd?.storeFaqs?.length;
        return faqCount > 0
          ? `Find answers to ${faqCount} frequently asked questions about ${name}.`
          : `Frequently asked questions about ${name} on ${mp}.`;
      }
      case 'blog':
        return `Read articles and updates from ${name} on ${mp}.`;
      case 'home':
      default: {
        // Guard against verbatim placeholder values.
        const DESC_PLACEHOLDERS = new Set([
          'description', 'desc', 'test', 'my description', 'store description', 'your description',
        ]);
        const rawDesc = stripHtml(pd?.seoDescription).trim();
        const isDescPlaceholder =
          !rawDesc || DESC_PLACEHOLDERS.has(rawDesc.toLowerCase());
        const cleanDesc = !isDescPlaceholder ? truncate155(rawDesc) : '';
        if (cleanDesc) return cleanDesc;
        if (aboutPlain.length > 0) return truncate155(aboutPlain);
        return c
          ? `Browse rentals from ${name} in ${c} on ${mp}.`
          : `Browse rentals from ${name} on ${mp}.`;
      }
    }
  };

  // hasBlogPosts: used for robots + schema; store blogs are fetched in StoreProfilePage.loadData (SSR + client).
  const hasBlogPosts = Array.isArray(storeBlogs) && storeBlogs.some(
    b => !b?.status || b.status === 'published'
  );

  const profilePageSchema = profileUser?.id
    ? buildStoreSchema({
        provider: profileUserWithCategories,
        storeUrl: storeBaseUrl,
        rootUrl,
        hasBlogPosts,
        averageRating: seoAverageRating,
        reviewCount: seoReviewCount,
      })
    : null;

  if (profilePageSchema?.mainEntity) {
    profilePageSchema.mainEntity.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: `Listings by ${storeName || displayName}`,
      url: `${storeBaseUrl}/listings`,
    };
  }

  const faqSchema = useMemo(() => {
    const faqs = publicData?.storeFaqs;
    if (!faqs?.length) return null;
    return buildFaqSchema(faqs);
  }, [publicData?.storeFaqs]);

  const gallerySchema = useMemo(() => {
    const storeMedia = publicData?.storeMedia;
    if (process.env.NODE_ENV !== 'production') {
      // TODO: remove after gallery-schema fix is verified
      // eslint-disable-next-line no-console
      console.log('[gallerySchema useMemo] storeMedia length:', storeMedia?.length ?? 'undefined');
    }
    if (!Array.isArray(storeMedia) || storeMedia.length === 0) return null;
    return buildGallerySchema({
      storeMedia,
      storeName: defaultStoreName,
      galleryUrl: buildStoreCanonicalUrl({
        rootUrl,
        storeName: storeName || displayName,
        city: authorizedCity,
        userId: storeUserId,
        activeTab: 'gallery',
      }),
    });
  }, [publicData?.storeMedia, defaultStoreName, rootUrl, storeName, displayName, authorizedCity, storeUserId]);

  const blogCollectionSchema = useMemo(() => {
    const toISODate = value => {
      if (!value) return null;
      const m = moment(value);
      return m.isValid() ? m.toISOString() : null;
    };

    const published = (storeBlogs || []).filter(
      b => !b?.status || b.status === 'published'
    );

    const posts = published
      .map(b => {
        const id = b?._id || b?.id;
        const listingId = b?.listingId;
        if (!id) return null;

        const isListingBlog = !!listingId;
        const path = isListingBlog
          ? createResourceLocatorString(
            'BlogContentPage',
            routes,
            { blogId: getStoreBlogId(b), listingId },
            {}
          )
          : createStoreBlogPath(b, storeAuthorId || 'unknown', routes);
        if (!path) return null;
        const url = `${config.marketplaceRootURL}${path}`;
        const title = b?.title || '';
        if (!title) return null;

        const imageUrl =
          b?.image?.secure_url ||
          b?.image?.imageUrl ||
          b?.imageUrl ||
          '';
        const datePublished = toISODate(
          b?.createdAt || b?.updatedAt || b?.publishDate
        );
        const dateModified =
          toISODate(b?.updatedAt) || toISODate(b?.publishDate) || toISODate(b?.createdAt);

        const imageWidth  = typeof b?.image?.width  === 'number' ? b.image.width  : null;
        const imageHeight = typeof b?.image?.height === 'number' ? b.image.height : null;
        return { title, url, imageUrl, imgWidth: imageWidth, imgHeight: imageHeight, datePublished, dateModified };
      })
      .filter(Boolean);

    const dedupedPosts = Array.from(
      new Map(posts.map(p => [p.url, p])).values()
    );

    if (dedupedPosts.length === 0) return null;

    const blogCollectionName = intl.formatMessage(
      {
        id: 'StoreProfilePage.blogCollection.name',
        defaultMessage: '{storeName} Blog',
      },
      { storeName: defaultStoreName }
    );
    const blogCollectionDescription = intl.formatMessage(
      {
        id: 'StoreProfilePage.blogCollection.description',
        defaultMessage:
          'Rental guides, tips, and insights from {storeName}',
      },
      { storeName: defaultStoreName }
    );

    return {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${storeBlogCollectionUrl}#blog`,
      url: storeBlogCollectionUrl,
      name: blogCollectionName,
      description: blogCollectionDescription,
      publisher: { '@id': `${config.marketplaceRootURL}#organization` },
      blogPost: dedupedPosts.map(p => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: p.url,
        ...(p.imageUrl ? {
          image: {
            '@type':    'ImageObject',
            url:         p.imageUrl,
            contentUrl:  p.imageUrl,
            ...(p.imgWidth  ? { width:  p.imgWidth  } : {}),
            ...(p.imgHeight ? { height: p.imgHeight } : {}),
          },
        } : {}),
        author: {
          '@type': 'Organization',
          name:    defaultStoreName,
          '@id':   `${storeBaseUrl}#local-business`,
        },
        ...(p.datePublished ? { datePublished: p.datePublished } : {}),
        ...(p.dateModified ? { dateModified: p.dateModified } : {}),
      })),
    };
  }, [
    config.marketplaceRootURL,
    defaultStoreName,
    routes,
    storeAuthorId,
    storeBlogs,
    storeBlogCollectionUrl,
    intl,
  ]);

  // ── Breadcrumb schema ────────────────────────────────────────────────────────
  const TAB_DISPLAY_NAMES = {
    about: 'About',
    reviews: 'Reviews',
    gallery: 'Gallery',
    faqs: 'FAQs',
    blog: 'Blog',
  };

  const breadcrumbSchema = useMemo(() => {
    const items = [
      { name: config.marketplaceName, url: rootUrl },
      { name: storeName || displayName, url: storeBaseUrl },
    ];
    if (activeTab !== 'home' && TAB_DISPLAY_NAMES[activeTab]) {
      items.push({ name: TAB_DISPLAY_NAMES[activeTab], url: canonicalUrl });
    }
    return buildBreadcrumbSchema(items);
  }, [activeTab, canonicalUrl, config.marketplaceName, displayName, rootUrl, storeBaseUrl, storeName]);

  // ── Client-side safety net: legacy UUID-only URL → slug+UUID ──────────────
  // Handles cases where server-side 301 was not reached (SPA navigation)
  if (isLegacyUrl && typeof window !== 'undefined' && profileUser?.id) {
    const legacySlug = generateStoreSlug(publicData?.storeName || displayName);
    const legacyCitySlug = generateCitySlug(publicData?.storeAddress?.city || publicData.city || null);
    const legacyBase = legacyCitySlug
      ? `/rental-owner/${legacyCitySlug}/${legacySlug}/${resolvedUserId}`
      : `/rental-owner/${legacySlug}/${resolvedUserId}`;
    const tabSegment = activeTab !== 'home' ? `/${activeTab}` : '';
    window.location.replace(`${legacyBase}${tabSegment}`);
    return null;
  }

  // ── Client-side safety net: ?tab= param → path-based tab URL ──────────────
  const tabFromParam = parse(location?.search || '').tab;
  const VALID_TABS_FOR_REDIRECT = ['about', 'reviews', 'gallery', 'faqs', 'blog'];
  if (tabFromParam && params.slug && params.id && typeof window !== 'undefined') {
    const tabSegment = VALID_TABS_FOR_REDIRECT.includes(tabFromParam) ? `/${tabFromParam}` : '';
    const profilePath = params.city
      ? `/rental-owner/${params.city}/${params.slug}/${params.id}`
      : `/rental-owner/${params.slug}/${params.id}`;
    window.location.replace(`${profilePath}${tabSegment}`);
    return null;
  }

  if (userShowError && userShowError.status === 404) {
    // 404 profile not found
    return <NotFoundPage />;
  } else if (userShowError) {
    // Genuine system error (not a deleted/missing store) - must not redirect
    // and must not look like everything worked.
    return (
      <Page
        title={intl.formatMessage({
          id: 'StoreProfilePage.errorLoadingProfileTitle',
          defaultMessage: 'Error in loading the profile',
        })}
        scrollingDisabled={scrollingDisabled}
      >
        <LayoutSingleColumn
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
          mobileBottomNavbar={<MobileBottomNavbar />}
        >
          <SystemErrorSection
            contextLabel={intl.formatMessage({ id: 'StoreProfilePage.errorLoadingProfileContext' })}
            onRetry={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
          />
        </LayoutSingleColumn>
      </Page>
    );
  } else if (userShowInProgress || (!user || (user && !user.id))) {
    // Still loading the Profile - use marketplace name for title so shared link preview doesn't show "Loading profile"
    const loadingTitle = config?.marketplaceName
      ? intl.formatMessage(
        {
          id: 'StoreProfilePage.loadingTitleWithMarketplace',
          defaultMessage: '{marketplaceName} | Store',
        },
        { marketplaceName: config.marketplaceName }
      )
      : intl.formatMessage({
        id: 'StoreProfilePage.loadingProfileTitle',
        defaultMessage: 'Loading profile',
      });
    return (
      <Page title={loadingTitle} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
          mobileBottomNavbar={<MobileBottomNavbar />}
        >
          <p className={css.loadingText}>
            <FormattedMessage id="StoreProfilePage.loadingProfileMessage" />
          </p>
        </LayoutSingleColumn>
      </Page>
    );
  }

  // ── Route-driven schema injection ──────────────────────────────────────────
  // Breadcrumb schema is added separately below via buildBreadcrumbSchema.
  const pageSchemas = (() => {
    const schemas = [profilePageSchema];

    switch (activeTab) {
      case 'home':
        if (faqSchema) schemas.push(faqSchema);
        if (blogCollectionSchema) schemas.push(blogCollectionSchema);
        break;
      case 'about':
        break;
      case 'reviews':
        break;
      case 'gallery':
        if (gallerySchema) schemas.push(gallerySchema);
        break;
      case 'faqs':
        if (faqSchema) schemas.push(faqSchema);
        break;
      case 'blog':
        if (blogCollectionSchema) schemas.push(blogCollectionSchema);
        break;
      default:
        break;
    }

    return schemas.filter(Boolean);
  })();

  // ── Robots directive ─────────────────────────────────────────────────────────
  const robotsDirective = buildTabRobotsDirective({
    activeTab,
    publicData,
    hasBlogPosts,
    reviewCount: seoReviewCount,
  });

  // ── Tab-specific title & description ──────────────────────────────────────────
  const titleDescArgs = { activeTab, publicData, marketplaceName: config.marketplaceName };
  const pageTitle = buildStorePageTitle(titleDescArgs);
  const pageDescription = buildStorePageDescription(titleDescArgs);

  // Blog tab: override og:image with the first published post's cover image
  // so social shares of the blog index show editorial content, not the store banner.
  if (activeTab === 'blog' && Array.isArray(storeBlogs) && storeBlogs.length > 0) {
    const firstPublished = storeBlogs.find(b => !b?.status || b.status === 'published');
    const blogOgRawUrl =
      firstPublished?.image?.secure_url ||
      firstPublished?.image?.imageUrl ||
      null;
    if (blogOgRawUrl) {
      const blogOgUrl = toOgImageUrl(blogOgRawUrl);
      const blogOgAlt = firstPublished?.title
        ? `${firstPublished.title} – ${defaultStoreName} blog`
        : coverAltText;
      socialSharing.images1200 = [{ url: blogOgUrl, width: 1200, height: 630, alt: blogOgAlt }];
      socialSharing.images600  = [{ url: blogOgUrl, width: 600,  height: 314, alt: blogOgAlt }];
    }
  }

  // Finalize socialSharing with derived title and description
  socialSharing.title = pageTitle;
  socialSharing.description = pageDescription;

  // ── og:type per tab ──────────────────────────────────────────────────────────
  const OG_TYPE_MAP = {
    home: 'website',
    about: 'website',
    reviews: 'website',
    gallery: 'website',
    faqs: 'website',
    blog: 'website',      // or 'article' if this renders individual posts
  };
  const openGraphType = OG_TYPE_MAP[activeTab] || 'website';

  // Merge breadcrumb into page schemas
  const finalSchemas = breadcrumbSchema
    ? [...pageSchemas, breadcrumbSchema]
    : pageSchemas;

  const twitterImageAlt = socialImageUrl
    ? (coverAltText ||
        `${defaultStoreName} rental store${authorizedCity ? ` in ${authorizedCity}` : ''} on ${config.marketplaceName}`)
    : pageTitle;

  return (
    <Page
      title={pageTitle}
      scrollingDisabled={scrollingDisabled}
      author={defaultStoreName}
      description={pageDescription}
      socialSharing={socialSharing}
      imageAlt={twitterImageAlt}
      openGraphType={openGraphType}
      canonicalUrl={canonicalUrl}
      robots={robotsDirective}
      schema={finalSchemas}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={
          <TopbarContainer
            onMarketplaceSearchIntercept={handleMarketplaceSearchIntercept}
          />
        }
        footer={<FooterContainer />}
        mobileBottomNavbar={<MobileBottomNavbar />}
      >
        <StoreLanguagePrompt
          storeId={profileUser?.id?.uuid}
          storeDefaultLanguage={
            storeDefaultLanguage &&
              SUPPORTED_LANGUAGES.includes(storeDefaultLanguage)
              ? storeDefaultLanguage
              : null
          }
        />
        {/* Notification Container for cart operations */}
        <NotificationContainer />

        {/* Header Component */}
        <Header
          user={translatedProfileUser}
          rating={rating}
          reviews={reviews}
          queryReviewsInProgress={queryReviewsInProgress}
          totalReviews={displayReviewCount}
          onShowReviews={() => { }}
          currentUserLocationInfo={currentUserLocationInfo}
          storeUrl={storeUrl}
          onContactUser={handleContactUser}
          usersEmailVerified={props.usersEmailVerified}
          currentUser={currentUser}
          onManageDisableScrolling={props.onManageDisableScrolling}
          locationSearch={location?.search || ''}
          locationPathname={location?.pathname || ''}
          activeTab={activeTab}
          datesSelected={
            !!(filters.dateRange?.startDate && filters.dateRange?.endDate)
          }
          isCurrentUser={isCurrentUser}
          onVerifyIdentityClick={handleVerifyIdentityClick}
          onVerifyIdentityClose={handleVerifyIdentityClose}
          isCustomizeCoverOpen={isCustomizeCoverOpen}
          onOpenCustomizeCover={handleOpenCustomizeCover}
          onCloseCustomizeCover={handleCloseCustomizeCover}
          storeCoverImage={storeCoverImage}
          storeCoverBackgroundColor={storeCoverBackgroundColor}
          storeBlogs={storeBlogs}
          storeBlogsFetchCompleted={storeBlogsFetchCompleted}
        />

        {activeTab === 'home' ? (
          <MainContent
            {...props}
            routes={routes}
            history={history}
            location={location}
            config={config}
            intl={intl}
            params={params}
            currentUser={currentUser}
            user={translatedProfileUser}
            storeUrl={storeUrl}
            storePath={storePath}
            isCurrentUser={isCurrentUser}
            userShowError={userShowError}
            userShowInProgress={userShowInProgress}
            filters={filters}
            onCategoryChange={handleCategoryChange}
            onDateRangeChange={handleDateRangeChange}
            onSearchQueryChange={handleSearchQueryChange}
            onListingFacetFiltersChange={handleListingFacetFiltersChange}
            defaultStoreName={defaultStoreName}
            storeLocation={storeLocation}
            storeAddress={storeAddress?.address || storeAddress}
            key={getMainContentKeyFromSearch(location.search) || 'main'}
            storeTimeZone={storeTimeZone}
            onAddToCart={handleAddToCart}
            onUpdateCart={handleUpdateCart}
            onUpdateRecordsOrder={onUpdateRecordsOrder}
            updateOrderError={updateOrderError}
            exchangeRates={exchangeRates}
            fetRatesError={fetRatesError}
            fetchRatesInProgress={fetchRatesInProgress}
            currentUserCurrency={currentUserCurrency}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onFetchTimeSlots={onFetchTimeSlots}
            filteredCartItems={filteredCartItems}
            dateRangePickerRef={dateRangePickerRef}
            datePickerAnchorRef={datePickerAnchorRef}
            searchSectionRef={searchSectionRef}
            pendingSearchSettledScrollRef={pendingSearchSettledScrollRef}
            onScrollToSearchSection={onScrollToSearchSection}
            onScrollToDateField={onScrollToDateField}
            onVerifyIdentityClick={handleVerifyIdentityClick}
            algoliaLocale={visitorLocale}
          />
        ) : (
          <StorefrontTabContent
            tab={activeTab}
            publicData={translatedProfileUser?.attributes?.profile?.publicData || publicData}
            locale={visitorLocale}
            reviews={reviews}
            totalReviews={displayReviewCount}
            userId={profileUser?.id}
            achievementReviews={achievementReviews}
            storeBlogs={storeBlogs}
            isCurrentUser={isCurrentUser}
            onReorderStoreMedia={onReorderStoreMedia}
            storeBlogsFetchCompleted={storeBlogsFetchCompleted}
          />
        )}

        {/* Cart Modal — owners see "Generate Quote" CTA; non-owners see "Rent Now" */}
        <CartModal
          isOpen={isCartModalOpen}
          onClose={closeCartModal}
          items={cartPanelItems}
          dateRange={cartPanelScope}
          hasOtherCartGroups={hasOtherCartGroups}
          onViewAllOrders={handleViewAllOrders}
          onSaveCartItemSelectionsBatch={props.onUpdateCartItemsBatch}
          onDatesApplied={handleCartDatesApplied}
          timeZone={storeTimeZone}
          intl={intl}
          onUpdateCart={handleUpdateCart}
          onRemoveFromCart={handleRemoveFromCart}
          onRentNow={handleRentNow}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          currentUserCurrency={currentUserCurrency}
          exchangeRates={exchangeRates}
          marketplaceName={config.marketplaceName}
          marketplaceCurrency={config.currency}
          listing={cartPanelItems[0]?.listing}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          isOwner={isCurrentUser}
          showAddressInStore={publicData.showAddressInStore === true}
        />
        {/* All-orders panel: one row per (store, date range) across the whole cart.
            Same component and navigation stack as the listing page - only one of it
            and the Store Cart panel is ever open. Rendered only once first needed so
            the chunk never loads for shoppers who never open the cart. */}
        {isAllOrdersPanelOpen ? (
          <AllCartItemsPanel
            isOpen
            onClose={closeCartModal}
            onManageDisableScrolling={props.onManageDisableScrolling}
            itemsByAuthor={cartItems || {}}
            onSelectGroup={handleSelectCartGroup}
            intl={intl}
          />
        ) : null}

        {isCurrentUser ? (
          <VerificationModel
            isOpen={isVerificationModalOpen}
            onClose={handleVerifyIdentityClose}
            onManageDisableScrolling={props.onManageDisableScrolling}
            usePortal
            isVerificationInProgress={isVerificationInProgress}
            setIsVerificationInProgress={setIsVerificationInProgress}
            onUpdateUserProfile={props.onUpdateUserProfile || (() => Promise.resolve())}
            currentUser={currentUser || profileUser}
            transactionId={null}
            onRefreshTransactionEntityAgain={null}
            isVerificationFailed={isVerificationFailed}
            setIsVerificationFailed={setIsVerificationFailed}
            history={history}
            routeConfiguration={routes}
          />
        ) : null}

        {isCurrentUser ? (
          <CustomizeStoreCoverModal
            isOpen={isCustomizeCoverOpen}
            onClose={handleCloseCustomizeCover}
            onSaveStoreCover={handleUpdateStoreCover}
            onManageDisableScrolling={props.onManageDisableScrolling}
            storeCoverImage={storeCoverImage}
            storeCoverBackgroundColor={storeCoverBackgroundColor}
            storeDisplayPhoto={storeDisplayPhoto}
            storeName={storeName || profileUser?.attributes?.profile?.displayName}
          />
        ) : null}

        <StoreSearchScopeModal
          isOpen={isSearchScopeModalOpen}
          storeName={defaultStoreName || ''}
          onClose={handleSearchScopeClose}
          onStayOnStore={handleStayOnStore}
          onSearchMarketplace={handleSearchMarketplace}
          onManageDisableScrolling={props.onManageDisableScrolling}
        />
      </LayoutSingleColumn>
    </Page>
  );
}

const mapStateToProps = state => {
  const {
    currentUser,
    currentUserCurrency,
    currentUserLocationInfo,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const { usersEmailVerified } = state.TransactionPage;
  const {
    userId,
    userShowError,
    userShowInProgress,
    reviewsReceived,
    totalReviewsReceived,
    reviewsReceivedInProgress,
    reviewsReceivedPagination,
    reviewsReceivedError,
    queryReviewsError,
    queryReviewsInProgress,
    queryListingsError,
    updateOrderError,
    achievementReviews,
    fetchUserAchievementsInProgress,
    fetchUserAchievementsError,
    userAchievements,
    isVerified,
    storeBlogs,
    fetchStoreBlogsInProgress,
    fetchStoreBlogsError,
    storeBlogsFetchCompleted,
  } = state.StoreProfilePage;
  const {
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
  } = state.exchangeRate;

  const cart = state.cart;
  const {
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
  } = state.ListingPage;

  const userMatches = getMarketplaceEntities(state, [
    { type: 'user', id: userId },
  ]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const totalListings = user?.attributes?.profile?.publicData?.totalListings ?? 0;

  return {
    user,
    userId,
    currentUser,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    userShowError,
    userShowInProgress,
    reviews: reviewsReceived,
    totalReviews: totalReviewsReceived,
    reviewsReceived,
    totalReviewsReceived,
    reviewsReceivedInProgress,
    reviewsReceivedPagination,
    reviewsReceivedError,
    achievementReviews,
    queryReviewsError,
    queryReviewsInProgress,
    totalListings,
    queryListingsError,
    scrollingDisabled: isScrollingDisabled(state),
    updateOrderError,
    usersEmailVerified: usersEmailVerified?.user || {},
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
    currentUserCurrency,
    currentUserLocationInfo, // Added for Header distance calculation
    cart,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    fetchUserAchievementsInProgress,
    fetchUserAchievementsError,
    userAchievements,
    isVerified,
    storeBlogs,
    fetchStoreBlogsInProgress,
    fetchStoreBlogsError,
    storeBlogsFetchCompleted,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  onUpdateRecordsOrder: data => dispatch(updateRecordsOrder(data)),
  onReorderStoreMedia: orderedMedia =>
    dispatch(reorderStoreMedia(orderedMedia)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onAddToCart: (config, params, queryID) =>
    dispatch(addToCart(config, params, queryID)),
  onUpdateCart: params => dispatch(updateCart(params)),
  onRemoveFromCart: itemId => dispatch(removeFromCart(itemId)),
  onUpdateCartItemsBatch: (itemIds, bookingDates) =>
    dispatch(updateCartItemsBatch(itemIds, bookingDates)),
  onFetchUserAchievements: userId => dispatch(fetchUserAchievements(userId)),
  onIsUsersEmailVerified: userId =>
    dispatch(isUsersEmailVerified(null, userId)),
  onFetchStoreBlogs: userId => dispatch(fetchStoreBlogsData(userId)),
  onUpdateUserProfile: payload => dispatch(updateStoreCover(payload)),
  onSendVerificationEmail: () => dispatch(sendVerificationEmail()),
  dispatch,
});

StoreProfilePageComponent.propTypes = {
  intl: intlShape.isRequired,
  location: PropTypes.object.isRequired,
  user: propTypes.user,
  history: PropTypes.object.isRequired,
  currentUser: propTypes.currentUser,
  userShowError: propTypes.error,
  userShowInProgress: PropTypes.bool,
  scrollingDisabled: PropTypes.bool,
  reviews: PropTypes.arrayOf(propTypes.review),
  totalReviews: PropTypes.number,
  reviewsReceived: PropTypes.arrayOf(propTypes.review),
  totalReviewsReceived: PropTypes.number,
  currentUserLocationInfo: PropTypes.shape({
    origin: propTypes.latlng,
    country: PropTypes.string,
  }),
  onAddToCart: PropTypes.func.isRequired,
  onUpdateCartItemsBatch: PropTypes.func.isRequired,
  exchangeRates: PropTypes.array,
  fetRatesError: propTypes.error,
  fetchRatesInProgress: PropTypes.bool,
  currentUserCurrency: PropTypes.string,
  onFetchTransactionLineItems: PropTypes.func.isRequired,
  onFetchTimeSlots: PropTypes.func.isRequired,
  params: PropTypes.shape({
    slug: PropTypes.string,
    city: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  onUpdateRecordsOrder: PropTypes.func.isRequired,
  onReorderStoreMedia: PropTypes.func.isRequired,
  updateOrderError: propTypes.error,
  onManageDisableScrolling: PropTypes.func.isRequired,
  usersEmailVerified: PropTypes.object,
  lineItems: propTypes.lineItems,
  fetchLineItemsInProgress: PropTypes.bool,
  fetchLineItemsError: PropTypes.error,
  achievementReviews: PropTypes.arrayOf(propTypes.review),
  storeBlogs: PropTypes.array,
  storeBlogsFetchCompleted: PropTypes.bool,
};

const StoreProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl,
  withRouter
)(StoreProfilePageComponent);

export default StoreProfilePage;
